import React, { forwardRef } from "react";
import { Controller, Control, FieldValues, Path } from "react-hook-form";

interface FormControllerProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  children: React.ReactElement;
}

function FormControllerInner<T extends FieldValues>(
  { control, name, children }: FormControllerProps<T>,
  ref: React.Ref<any>,
) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) =>
        React.cloneElement(children, {
          ...field,
          value: field.value ?? "", // Ensure value is set properly, fallback to empty string
          onSelectionChange: (
            selectedIds: Array<string | number> | string | number,
          ) => {
            field.onChange(selectedIds); // Syncs with React Hook Form
          },
          onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
            field.onChange(event); // React Hook Form's onChange
            if (children.props.onChange) {
              children.props.onChange(event); // Parent's onChange
            }
          },
          onBlur: (event: React.ChangeEvent<HTMLInputElement>) => {
            field.onBlur(); // React Hook Form's onBlur
            if (children.props.onBlur) {
              children.props.onBlur(event); // Parent's onBlur
            }
          },
          ref, // Attach the ref correctly
          error: fieldState?.error,
          helperText: fieldState?.error?.message,
          helperTextType: fieldState?.error?.type,
        })
      }
    />
  );
}

// Wrap it with `forwardRef` without generic arguments
export const FormController = forwardRef(FormControllerInner) as <
  T extends FieldValues,
>(
  props: FormControllerProps<T> & { ref?: React.Ref<any> },
) => ReturnType<typeof FormControllerInner>;
